import { FC, Suspense, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  styled,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import ConsumerAccountsModal from './ConsumerAccountsModal';
import LinkModal from './LinkModal/LinkModal';
import { StoreSelector } from '../components/StoreSelector';
import { CartItemModal } from './CartItemModal';
import { NoteModal } from './NoteModal';
import { VerticalSpace } from '@chargeafter/ui';
import { ButtonName } from '../constants';
import { TrackerIconButton } from '../components/TrackerButtonWrapper';
import { AppView } from '../constants';
import { camelCaseToPascalCase } from '../formattingUtils';
import { useSetPageName } from '../services/flowManager';
import { ModalTypes, useGetCurrentModal } from '../services/modalManager';
import { IdleTimeModal } from './IdleTimeModal';
import ChangePasswordModal from './ChangePasswordModal';

const StyledCloseButton = styled(TrackerIconButton)(({ theme }) => ({
  zIndex: 1,
  position: 'absolute',
  right: 8,
  top: 8,
  color: theme.palette.grey[500],
  '& > svg': {
    fontSize: '1.2rem',
  },
}));

type ModalProps = {};

const StyledLoaderBox = styled(Box)({
  display: 'flex',
  height: 538,
  width: '93vw',
  justifyContent: 'center',
  alignItems: 'center',
});

const Modal: FC<ModalProps> = () => {
  const currentModal: ModalTypes | undefined = useGetCurrentModal();
  const [close, setClose] = useState<(() => void) | null>(null);
  const updateCloseFunc = (func: () => void) => setClose(func);
  const setPageName = useSetPageName();
  const pageName =
    AppView[camelCaseToPascalCase(currentModal) as keyof typeof AppView];

  useEffect(() => {
    setPageName(pageName);
  }, []);

  useEffect(() => {
    if (!currentModal) setClose(null);
  }, [currentModal]);

  return (
    <Dialog open={!!currentModal}>
      {close && (
        <StyledCloseButton
          buttonName={ButtonName.Close}
          onClick={() => !!close && close()}
          data-testid="modal-x-button"
        >
          <Close />
        </StyledCloseButton>
      )}
      <DialogContent>
        <Suspense
          fallback={
            <StyledLoaderBox>
              <CircularProgress />
            </StyledLoaderBox>
          }
        >
          <ModalContent
            type={currentModal}
            close={close}
            updateClose={updateCloseFunc}
          />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
};

type CurrentModalProps = {
  type?: ModalTypes;
  close: (() => void) | null;
  updateClose: (func: () => void) => void;
};

const ModalContent: FC<CurrentModalProps> = ({ type, close, updateClose }) => {
  switch (type) {
    case 'consumerAccounts':
      return (
        <ConsumerAccountsModal
          close={close as () => void}
          updateClose={updateClose}
        />
      );
    case 'sendLink':
      return (
        <LinkModal close={close as () => void} updateClose={updateClose} />
      );
    case 'storeSelector':
      return (
        <StoreSelector close={close as () => void} updateClose={updateClose} />
      );
    case 'cartItem':
      return (
        <CartItemModal close={close as () => void} updateClose={updateClose} />
      );
    case 'idleTime':
      return <IdleTimeModal />;
    case 'applyDisabled':
    case 'checkoutDisabled':
    case 'checkoutLenderDisabled':
      return (
        <NoteModal
          showWarningIcon
          title="Please note"
          content={
            <Box>
              <Typography fontWeight="bold">
                {`${
                  type !== 'applyDisabled' ? 'Checkout' : 'Apply'
                } is currently not available through the app${
                  type === 'checkoutLenderDisabled'
                    ? ' with the selected lender provider'
                    : ''
                }.`}
              </Typography>
              <VerticalSpace units={2} />
              <Typography variant="body2">
                {`${
                  type === 'checkoutLenderDisabled'
                    ? 'For next steps on how to use the available balance, please reach out to the sales representatives experts.'
                    : 'User unauthorized'
                }`}
              </Typography>
            </Box>
          }
          close={close as () => void}
          updateClose={updateClose}
        />
      );
    case 'changePassword':
      return (
        <ChangePasswordModal
          close={close as () => void}
          updateClose={updateClose}
        />
      );

    default:
      return null;
  }
};
export default Modal;
